import { useDroppable } from "@dnd-kit/core";
import { checkPermissions } from "@elit-hair/elit-tools";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid";
import { GridCellSelectionModel, GridEventListener, useGridApiRef } from "@mui/x-data-grid-premium";
import { GridRowParams } from "@mui/x-data-grid-pro";
import { GridRow, GridRowProps } from "@mui/x-data-grid/components/GridRow";
import { motion } from "motion/react";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import CommonGridTable from "@/components/CommonGridTable";
import PassengerCountColumn from "@/components/table/PassengerCountColumn";
import orderColumn from "@/components/table/orderColumn";
import { HotelClinicArrivalTableProps } from "@/constants/hotelClinicArrivalSections";
import { PERMISSIONS } from "@/constants/permissions";
import { useClinicArrivalTasksPickUpSaveAction } from "@/hooks/useClinicArrivalTasksPickUpSaveAction";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import HotelNameColumn from "@/pages/HotelPlanning/arrival/components/table/components/HotelNameColumn";
import useAuth from "@/providers/hooks/useAuth";
import {
    CLINIC_ARRIVAL_TASK_DROP_PREFIX,
    ClinicArrivalOverTargetType,
    useClinicArrivalDndOverTaskId,
} from "@/stores/use-clinic-arrival-dnd";
import {
    useClinicArrivalTasksGridActions,
    useClinicArrivalTasksGridExpandedRows,
    useClinicArrivalTasksIsFullScreen,
    useClinicArrivalTasksRowModesModel,
    useClinicArrivalTasksStatusFilter,
} from "@/stores/use-clinic-arrival-tasks-grid";
import { onGridCellKeyDownCallback } from "@/utils/grid";

import { HotelClinicTaskResolved } from "../../types";
import ClinicArrivalTaskDriverColumn from "./ClinicArrivalTaskDriverColumn";
import { ClinicArrivalTaskPickupColumn } from "./ClinicArrivalTaskPickupColumn";
import ClinicArrivalTaskStatusHistoryDialog from "./ClinicArrivalTaskStatusHistoryDialog";
import ClinicArrivalTasksActionsColumn from "./ClinicArrivalTasksActionsColumn";
import ClinicArrivalTasksBulkConfirmDialog from "./ClinicArrivalTasksBulkConfirmDialog";
import { ClinicArrivalTasksStatusFilter } from "./ClinicArrivalTasksStatusFilter";
import TasksTableDetailPanelColumn from "./TasksGridDetailPanelColumn";
import TasksStatusColumn from "./TasksStatusColumn";
import TasksTableToolbar from "./TasksTableToolbar";
import { defaultTasksTableGridOptions } from "./defaultTasksTableGridOptions";
import TasksDetailTable from "./tasksDetailTable";

const DroppableTaskRow = React.forwardRef<HTMLDivElement, GridRowProps>(function DroppableTaskRow(props, outerRef) {
    const taskId = String(props.row?.id ?? "");

    const { setNodeRef } = useDroppable({
        id: `${CLINIC_ARRIVAL_TASK_DROP_PREFIX}${taskId}`,
        data: {
            type: ClinicArrivalOverTargetType.Task,
            taskId: taskId,
            zone: "outer",
        },
    });

    const overTaskId = useClinicArrivalDndOverTaskId();
    const highlight = overTaskId === taskId;

    const mergedRef = (el: HTMLDivElement | null) => {
        setNodeRef(el);
        if (typeof outerRef === "function") outerRef(el);
        else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    };

    return (
        <GridRow
            ref={mergedRef}
            {...props}
            style={{
                ...props.style,
                backgroundColor: highlight ? "rgba(20,184,166,0.07)" : undefined,
                boxShadow: highlight
                    ? "inset 2px 0 0 0 rgba(20,184,166,0.7), inset -2px 0 0 0 rgba(20,184,166,0.7), inset 0 2px 0 0 rgba(20,184,166,0.7)"
                    : "none",
                transition: "background-color 150ms ease, box-shadow 150ms ease",
            }}
        />
    );
});

function TasksTable({
    rows,
    allRows,
    loading = false,
    ...commonStatusProps
}: HotelClinicArrivalTableProps<HotelClinicTaskResolved>) {
    const apiRef = useGridApiRef();
    const { updateSelectedRows, setExpandedRows, processRowUpdate, handleRowModesModelCancel } =
        useClinicArrivalTasksGridActions();
    const expandedRows = useClinicArrivalTasksGridExpandedRows();
    const statusFilter = useClinicArrivalTasksStatusFilter();
    const isFullScreen = useClinicArrivalTasksIsFullScreen();
    const rowModesModel = useClinicArrivalTasksRowModesModel();
    const { size } = useWindowDimensions();
    const { t } = useTranslation();

    const selectionModel = useRef<GridCellSelectionModel | undefined>(undefined);
    const setCellSelectionModel = useCallback((selection: GridCellSelectionModel) => {
        selectionModel.current = selection;
    }, []);

    const userPermissions = useAuth()?.userData?.permissionCodes || [];
    const canUpdate = checkPermissions(userPermissions, [PERMISSIONS.TR_SVC_MANAGE_DRIVERS]);

    const {
        handleSaveChanges: handleSavePickUpChanges,
        saveDisabled: savePickUpDisabled,
        saveConfirmOpen,
    } = useClinicArrivalTasksPickUpSaveAction(apiRef);

    const filteredRows = useMemo(() => {
        if (!statusFilter) return rows;
        return rows.filter((r) => r.status === statusFilter);
    }, [rows, statusFilter]);

    const onCellKeyDown = useCallback<GridEventListener<"cellKeyDown">>(
        ({ cellMode, hasFocus, field, isEditable, id }, event, ctx) => {
            onGridCellKeyDownCallback(
                { cellMode, hasFocus, field, isEditable },
                event,
                ctx,
                selectionModel.current || {},
                processRowUpdate,
                handleRowModesModelCancel,
                "pickUp"
            );
            if (event.key !== "Enter" || event.repeat || saveConfirmOpen) return;

            if (cellMode === "edit") {
                event.preventDefault();
                ctx.api.stopCellEditMode({ id, field });
                queueMicrotask(() => {
                    handleSavePickUpChanges();
                });
                return;
            }

            if (savePickUpDisabled) return;
            event.preventDefault();
            handleSavePickUpChanges();
        },
        [handleRowModesModelCancel, handleSavePickUpChanges, processRowUpdate, savePickUpDisabled, saveConfirmOpen]
    );

    const columns = useMemo(
        () => [
            TasksTableDetailPanelColumn({ rows: filteredRows }),
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                headerClassName: "checkbox-header",
            },
            orderColumn(),
            TasksStatusColumn({ t }),
            HotelNameColumn({ t }),
            ClinicArrivalTaskPickupColumn({ t, editable: canUpdate }),
            PassengerCountColumn({ t }),
            ClinicArrivalTaskDriverColumn({ t }),
            ClinicArrivalTasksActionsColumn({ allRows: rows, t }),
        ],
        [canUpdate, filteredRows, t, rows]
    );

    const getDetailPanelContent = useCallback(({ row }: GridRowParams) => {
        return <TasksDetailTable task={row as HotelClinicTaskResolved} />;
    }, []);

    const tasksGridSx = useMemo(
        () =>
            isFullScreen
                ? ({
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                      width: "100%" as const,
                  } as const)
                : {
                      borderTop: "0px !important",
                  },
        [isFullScreen]
    );

    return (
        <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
            <ClinicArrivalTasksBulkConfirmDialog />
            <ClinicArrivalTaskStatusHistoryDialog tasks={rows} />
            <motion.div
                className={
                    isFullScreen
                        ? "flex min-h-0 h-full min-w-0 flex-1 flex-col"
                        : "flex min-h-0 min-w-0 flex-1 flex-col"
                }
                initial={{ position: "static", width: "100%" }}
                animate={{
                    height: isFullScreen ? "100vh" : "auto",
                    width: isFullScreen ? "100vw" : "100%",
                    position: isFullScreen ? "fixed" : "static",
                    padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                    top: 0,
                    left: 0,
                    background: isFullScreen ? "white" : "transparent",
                    zIndex: isFullScreen ? 1000 : "auto",
                }}
                transition={{ duration: 0.3 }}
            >
                <div
                    className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm ${
                        isFullScreen ? "h-full" : ""
                    }`}
                >
                    <CommonGridTable<HotelClinicTaskResolved>
                        api={apiRef}
                        loading={loading}
                        rows={filteredRows}
                        allRows={rows}
                        columns={columns}
                        checkboxSelection
                        initialState={defaultTasksTableGridOptions}
                        gridSx={tasksGridSx}
                        toolbar={() => (
                            <TasksTableToolbar
                                rows={filteredRows}
                                apiRef={apiRef}
                                allRows={allRows}
                                {...commonStatusProps}
                            />
                        )}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        getDetailPanelContent={getDetailPanelContent}
                        onRowSelectionModelChange={updateSelectedRows}
                        detailPanelExpandedRowIds={expandedRows}
                        onDetailPanelExpandedRowIdsChange={setExpandedRows}
                        CustomGridRow={DroppableTaskRow as React.ComponentType<GridRowProps>}
                        startElement={ClinicArrivalTasksStatusFilter}
                        rowModesModel={rowModesModel}
                        processRowUpdate={processRowUpdate}
                        preventCellEdit={!canUpdate}
                        onCellKeyDown={onCellKeyDown}
                        onCellSelectionModelChange={setCellSelectionModel}
                    />
                </div>
            </motion.div>
        </div>
    );
}

export default TasksTable;
